import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  getUniqueNameValidation,
  isInvalidFilterNameToken,
} from "../../services/utilities/utility";

const maxFilterNameLength = 20;
const maxDescriptionLength = 100;

interface SaveFilterDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (
    filterShortcutName: string,
    isShortcut: boolean,
    description?: string,
    image?: File,
  ) => void;
  existingFilterNames?: string[];
}
const SaveFilterDialog: React.FC<SaveFilterDialogProps> = ({
  open,
  onClose,
  onSave,
  existingFilterNames = [],
}) => {
  const [filterName, setFilterName] = useState<string>("");
  const [isShortcut, setIsShortcut] = useState<boolean>(false);
  const [description, setDescription] = useState<string>("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const nameValidation = getUniqueNameValidation({
    name: filterName,
    existingNames: existingFilterNames,
  });
  const isFilterNameTooLong = filterName.length > maxFilterNameLength;
  const hasInvalidFilterNameToken = isInvalidFilterNameToken(filterName);
  const isDescriptionTooLong = description.length > maxDescriptionLength;

  const handleSave = async () => {
    if (
      filterName.trim() &&
      !hasInvalidFilterNameToken &&
      !nameValidation.isDuplicate &&
      !isFilterNameTooLong &&
      !isDescriptionTooLong
    ) {
      onSave(
        filterName.trim(),
        isShortcut,
        description.trim(),
        image ?? undefined,
      );
      handleClose();
    }
  };

  const handleClose = () => {
    setFilterName("");
    setIsShortcut(false);
    setDescription("");
    setImage(null);
    setImagePreview(null);
    onClose();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const allowedFormats = ["image/png", "image/jpeg"];
    setImageError("");
    if (file) {
      if (!allowedFormats.includes(file.type)) {
        setImageError("Only PNG, JPEG, and JPG formats are allowed.");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setImage(null);
    setImagePreview(null);
    setImageError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Save Filter</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Filter / Shortcut Name"
            fullWidth
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            placeholder="Filter / Shortcut Name"
            required
            error={
              hasInvalidFilterNameToken ||
              isFilterNameTooLong ||
              nameValidation.showDuplicateError
            }
            helperText={
              hasInvalidFilterNameToken
                ? 'Filter/Shortcut name cannot be null, "", or \"\".'
                : isFilterNameTooLong
                  ? `Filter/Shortcut name cannot exceed ${maxFilterNameLength} characters.`
                  : nameValidation.helperText
            }
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter description"
            error={isDescriptionTooLong}
            helperText={
              isDescriptionTooLong
                ? `Description cannot exceed ${maxDescriptionLength} characters.`
                : ""
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isShortcut}
                onChange={(e) => setIsShortcut(e.target.checked)}
              />
            }
            label="Create Shortcut"
          />
          {isShortcut && (
            <Box
              sx={{
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                textAlign: "center",
                cursor: "pointer",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                hidden
                onChange={handleImageChange}
              />
              {imageError && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {imageError}
                </Typography>
              )}
              {imagePreview ? (
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Shortcut image"
                    sx={{ maxWidth: "100%", maxHeight: 150, borderRadius: 1 }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                    sx={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      bgcolor: "background.paper",
                      boxShadow: 1,
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box>
                  <CloudUploadIcon
                    sx={{ fontSize: 40, color: "action.disabled", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Click to upload shortcut image (PNG, JPEG, JPG only)
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined" color="primary">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={
            !filterName.trim() ||
            hasInvalidFilterNameToken ||
            isFilterNameTooLong ||
            nameValidation.isDuplicate ||
            isDescriptionTooLong
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveFilterDialog;
